/* Home LP sections (sections/home-lp-*.liquid). Each section root is a <hlp-section>
   custom element, so theme editor reloads re-run connectedCallback and
   disconnectedCallback drops observers, timers and listeners. Features are opt-in by
   markup: .hlp-rv (entrance), [data-hlp-card] (tap-to-open "+" names),
   [data-hlp-hero] (video), [data-hlp-short] (one dropdown open) and
   [data-hlp-lines] (line tabs). */
(function () {
  if (customElements.get('hlp-section')) return;

  var HOVER_QUERY = '(hover: hover)';
  var MOBILE_QUERY = '(max-width: 860px)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var HOT_OPEN_MS = 5000;
  var SWIPE_MIN_PX = 45;
  var HOT_LABEL_GAP_PX = 12;
  var HOT_LABEL_SIDE_PX = 44;

  function canHover() {
    return window.matchMedia(HOVER_QUERY).matches;
  }

  // Keep a "+" name inside its photo, 12px from the edges. Sideways names (hover screens) flip
  // to the left of the "+" or, when neither side fits, open below it like on touch screens;
  // names below the "+" slide sideways to stay inside.
  function fitHotLabel(hot) {
    var label = hot.querySelector('.hlp-hot__lbl');
    var box = hot.offsetParent;
    if (!label || !box || !hot.offsetWidth) return;
    hot.classList.remove('hlp-hot--left', 'hlp-hot--below');
    label.style.removeProperty('--hlp-lbl-x');
    label.style.setProperty('--hlp-lbl-max', Math.max(box.clientWidth - HOT_LABEL_GAP_PX * 2, 0) + 'px');
    var boxRect = box.getBoundingClientRect();
    var hotRect = hot.getBoundingClientRect();
    var minX = boxRect.left + HOT_LABEL_GAP_PX;
    var maxX = boxRect.right - HOT_LABEL_GAP_PX;
    var width = label.offsetWidth;
    var opensSideways = canHover() && !hot.closest('.hlp-prod');
    if (opensSideways) {
      if (hotRect.left + HOT_LABEL_SIDE_PX + width <= maxX) return;
      if (hotRect.right - HOT_LABEL_SIDE_PX - width >= minX) { hot.classList.add('hlp-hot--left'); return; }
      hot.classList.add('hlp-hot--below');
      width = label.offsetWidth;
    }
    var left = hotRect.left + hotRect.width / 2 - width / 2;
    var shift = 0;
    if (left < minX) shift = minX - left;
    else if (left + width > maxX) shift = maxX - (left + width);
    if (shift) label.style.setProperty('--hlp-lbl-x', Math.round(shift) + 'px');
  }

  class HlpSection extends HTMLElement {
    connectedCallback() {
      this.cleanups = [];
      this.setupReveal();
      this.setupHotspots();
      this.setupHero();
      this.setupShortcuts();
      this.setupLines();
    }

    disconnectedCallback() {
      (this.cleanups || []).forEach(function (fn) { fn(); });
      this.cleanups = [];
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(function () { target.removeEventListener(type, handler, options); });
    }

    // Sections fade in once a bit of them is on screen. In the editor they show at once.
    setupReveal() {
      var items = Array.prototype.slice.call(this.querySelectorAll('.hlp-rv'));
      if (this.classList.contains('hlp-rv')) items.push(this);
      if (!items.length) return;
      var showAll = function () { items.forEach(function (el) { el.classList.add('in'); }); };
      if (window.Shopify && window.Shopify.designMode) { showAll(); return; }
      if (!('IntersectionObserver' in window)) { showAll(); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        });
      }, { threshold: 0.12 });
      items.forEach(function (el) { io.observe(el); });
      this.cleanups.push(function () { io.disconnect(); });
    }

    // Touch screens: the first tap on "+" shows the name, a tap on the name or photo follows the link.
    setupHotspots() {
      this.setupHotLabels();
      if (!this.querySelector('[data-hlp-card]')) return;
      var timer = null;
      this.on(this, 'click', function (event) {
        if (canHover()) return;
        var card = event.target.closest('[data-hlp-card]');
        var hot = event.target.closest('.hlp-hot');
        if (!card || !hot || event.target.closest('.hlp-hot__lbl')) return;
        event.preventDefault();
        var wasOpen = card.classList.contains('hot-open');
        document.querySelectorAll('.hot-open').forEach(function (el) { el.classList.remove('hot-open'); });
        clearTimeout(timer);
        if (wasOpen) return;
        card.classList.add('hot-open');
        fitHotLabel(hot);
        timer = setTimeout(function () { card.classList.remove('hot-open'); }, HOT_OPEN_MS);
      });
      this.cleanups.push(function () { clearTimeout(timer); });
    }

    // Place every "+" name now, when the photo size or the fonts change, and right before it shows.
    setupHotLabels() {
      var self = this;
      if (!this.querySelector('.hlp-hot__lbl')) return;
      var fitAll = function () { self.querySelectorAll('.hlp-hot').forEach(fitHotLabel); };
      fitAll();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(fitAll);
        ro.observe(this);
        this.cleanups.push(function () { ro.disconnect(); });
      }
      var fitInside = function (event) {
        var area = event.target.closest('[data-hlp-card], .hlp-hotwrap, .hlp-hot');
        if (!area) return;
        if (area.classList.contains('hlp-hot')) fitHotLabel(area);
        else area.querySelectorAll('.hlp-hot').forEach(fitHotLabel);
      };
      this.on(this, 'mouseover', fitInside);
      this.on(this, 'focusin', fitInside);
    }

    // The banner video loads and plays only while on screen; phones get the lighter file.
    setupHero() {
      var video = this.querySelector('[data-hlp-hero] video');
      if (!video) return;
      // Reduced motion: keep the poster only (no autoplaying video without a pause control).
      if (window.matchMedia(REDUCED_MOTION_QUERY).matches) { video.remove(); return; }
      video.muted = true;
      video.setAttribute('playsinline', '');
      var src = window.matchMedia(MOBILE_QUERY).matches && video.dataset.srcMobile ? video.dataset.srcMobile : video.dataset.src;
      var show = function () { video.classList.add('is-ready'); };
      this.on(video, 'loadeddata', show);
      this.on(video, 'playing', show);
      var play = function () {
        if (src && video.getAttribute('src') !== src) {
          video.setAttribute('src', src);
          video.load();
        }
        var promise = video.play();
        if (promise && promise.catch) promise.catch(function () {});
      };
      if (!('IntersectionObserver' in window)) { play(); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) play();
          else if (!video.paused) video.pause();
        });
      }, { threshold: 0.25 });
      io.observe(video);
      this.cleanups.push(function () { io.disconnect(); });
    }

    // Shortcut dropdowns are <details>: keep one open, close on outside click or Esc.
    setupShortcuts() {
      var root = this.querySelector('[data-hlp-short]');
      if (!root) return;
      var all = Array.prototype.slice.call(root.querySelectorAll('details'));
      var closeAll = function (except) {
        all.forEach(function (d) { if (d !== except) d.removeAttribute('open'); });
      };
      all.forEach(function (d) {
        this.on(d, 'toggle', function () { if (d.open) closeAll(d); });
      }, this);
      this.on(document, 'click', function (event) {
        if (!root.contains(event.target)) closeAll(null);
      });
      this.on(root, 'keydown', function (event) {
        if (event.key !== 'Escape') return;
        var open = root.querySelector('details[open]');
        if (!open) return;
        open.removeAttribute('open');
        open.querySelector('summary').focus();
      });
    }

    // Line tabs: chips, arrow keys, the "next" button, swipe on the photo and editor block select.
    setupLines() {
      var root = this.querySelector('[data-hlp-lines]');
      if (!root) return;
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      var self = this;
      var current = 0;
      var select = function (index, focus) {
        current = (index + tabs.length) % tabs.length;
        tabs.forEach(function (tab, i) {
          var on = i === current;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
        });
        root.querySelectorAll('[data-hlp-line-index]').forEach(function (el) {
          var on = Number(el.dataset.hlpLineIndex) === current;
          if (el.getAttribute('role') === 'tabpanel') el.hidden = !on;
          else el.classList.toggle('is-on', on);
        });
        root.querySelectorAll('.hlp-sdot').forEach(function (dot, i) { dot.classList.toggle('is-on', i === current); });
        if (focus) tabs[current].focus();
      };
      tabs.forEach(function (tab, i) {
        self.on(tab, 'click', function () { select(i, false); });
        self.on(tab, 'mouseenter', function () { if (canHover()) select(i, false); });
      });
      this.on(root, 'keydown', function (event) {
        if (!event.target.matches('[role="tab"]')) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); select(current + 1, true); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); select(current - 1, true); }
      });
      this.on(root, 'click', function (event) {
        if (event.target.closest('[data-hlp-next]')) select(current + 1, false);
      });
      var stage = root.querySelector('.hlp-line');
      var startX = null;
      var startY = null;
      if (stage) {
        this.on(stage, 'touchstart', function (event) {
          var t = event.touches[0];
          startX = t ? t.clientX : null;
          startY = t ? t.clientY : null;
        }, { passive: true });
        this.on(stage, 'touchend', function (event) {
          var t = event.changedTouches[0];
          if (startX === null || !t) return;
          var dx = t.clientX - startX;
          var dy = t.clientY - startY;
          if (Math.abs(dx) > SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy) * 1.4) select(current + (dx < 0 ? 1 : -1), false);
          startX = null;
        }, { passive: true });
      }
      this.on(document, 'shopify:block:select', function (event) {
        var i = tabs.findIndex(function (tab) { return tab.dataset.blockId === event.detail.blockId; });
        if (i > -1) select(i, false);
      });
      select(0, false);
    }
  }

  customElements.define('hlp-section', HlpSection);
})();
